import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiCheck,
  FiGlobe,
  FiImage,
  FiLoader,
  FiSave,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { richTextToPlainText } from '../utils/richText';
import { resolveServiceMediaUrl } from '../utils/serviceContent';
import ProductRichTextEditor from './ProductRichTextEditor';
import './AdminProductDescriptionTranslation.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];

const getWorkDescription = (translation, workIndex) => (
  translation?.data?.works?.[workIndex]?.description || ''
);

const emitSaveState = (serviceId, locale, fieldPath, state) => {
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType: 'service',
      entityId: String(serviceId),
      locale,
      fieldPath,
      state,
    },
  }));
};

const AdminServiceWorkDescriptionTranslation = ({
  serviceId,
  workIndex,
  workId,
  workTitle = '',
  sourceDescription = '',
  onClose,
}) => {
  const fieldPath = `works.${workIndex}.description`;
  const [loading, setLoading] = useState(true);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [value, setValue] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const saveTimers = useRef({});
  const pendingSaves = useRef({});
  const saveChains = useRef({});
  const requestNumbers = useRef({});
  const activeLocaleRef = useRef('');
  const mountedRef = useRef(true);

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);
  const sourceText = richTextToPlainText(sourceDescription);
  const sourceExcerpt = sourceText.slice(0, 220);

  const updateLocalTranslation = useCallback((localeCode, description, extra = {}) => {
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      const nextData = JSON.parse(JSON.stringify(existing?.data || {}));
      if (!Array.isArray(nextData.works)) nextData.works = [];
      if (!nextData.works[workIndex] || typeof nextData.works[workIndex] !== 'object') {
        nextData.works[workIndex] = {};
      }
      nextData.works[workIndex].description = description;
      const updated = {
        ...(existing || { locale: localeCode, status: 'draft' }),
        data: nextData,
        hasUnpublishedChanges: true,
        ...extra,
      };
      return existing
        ? items.map((item) => (item === existing ? updated : item))
        : [...items, updated];
    });
  }, [workIndex]);

  const persistPending = useCallback(async (localeCode) => {
    const pending = pendingSaves.current[localeCode];
    if (!pending) {
      try {
        await saveChains.current[localeCode];
        return true;
      } catch (_) {
        return false;
      }
    }

    delete pendingSaves.current[localeCode];
    if (activeLocaleRef.current === localeCode && mountedRef.current) setSaveState('saving');
    emitSaveState(serviceId, localeCode, fieldPath, 'saving');
    const previous = saveChains.current[localeCode] || Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => api.patch(`/api/services/${serviceId}/translations/${localeCode}/field`, {
        path: fieldPath,
        value: pending.value,
      }));
    saveChains.current[localeCode] = request;

    try {
      const response = await request;
      emitSaveState(serviceId, localeCode, fieldPath, 'saved');
      if (!mountedRef.current) return true;
      updateLocalTranslation(localeCode, pending.value, {
        status: response.data?.status || 'draft',
        updatedAt: response.data?.updatedAt || new Date().toISOString(),
      });
      if (
        activeLocaleRef.current === localeCode
        && requestNumbers.current[localeCode] === pending.requestNumber
        && !pendingSaves.current[localeCode]
      ) {
        setSaveState('saved');
      }
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'service', entityId: serviceId },
      }));
      return true;
    } catch (saveError) {
      emitSaveState(serviceId, localeCode, fieldPath, 'error');
      if (mountedRef.current) {
        if (activeLocaleRef.current === localeCode) setSaveState('error');
        setError(saveError.data?.message || saveError.message || 'ذخیره پیش‌نویس توضیحات نمونه‌کار انجام نشد');
      }
      return false;
    }
  }, [fieldPath, serviceId, updateLocalTranslation]);

  const flushLocale = useCallback(async (localeCode) => {
    if (!localeCode) return true;
    if (saveTimers.current[localeCode]) {
      clearTimeout(saveTimers.current[localeCode]);
      delete saveTimers.current[localeCode];
    }
    return persistPending(localeCode);
  }, [persistPending]);

  const scheduleSave = (nextValue, localeCode) => {
    if (!localeCode) return;
    const requestNumber = (requestNumbers.current[localeCode] || 0) + 1;
    requestNumbers.current[localeCode] = requestNumber;
    pendingSaves.current[localeCode] = { value: nextValue, requestNumber };
    if (saveTimers.current[localeCode]) clearTimeout(saveTimers.current[localeCode]);
    setSaveState('waiting');
    emitSaveState(serviceId, localeCode, fieldPath, 'waiting');
    setError('');
    updateLocalTranslation(localeCode, nextValue);
    saveTimers.current[localeCode] = setTimeout(() => {
      delete saveTimers.current[localeCode];
      persistPending(localeCode);
    }, 700);
  };

  const selectLocale = async (localeCode) => {
    if (!localeCode || localeCode === activeLocaleRef.current || switching) return;
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    if (!saved) {
      setSwitching(false);
      return;
    }
    activeLocaleRef.current = localeCode;
    setActiveLocale(localeCode);
    setValue(getWorkDescription(translations.find((item) => item.locale === localeCode), workIndex));
    setSaveState('idle');
    setError('');
    setSwitching(false);
  };

  const uploadImage = async (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
    }
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
    }
    if (!activeLocale) throw new Error('ابتدا زبان توضیحات را انتخاب کنید.');

    const formData = new FormData();
    formData.append('image', file);
    const response = await api.upload('/api/services/upload-image', formData);
    const imageUrl = response.data?.imageUrl;
    if (!response.success || !imageUrl) throw new Error('آپلود تصویر انجام نشد.');
    return resolveServiceMediaUrl(imageUrl);
  };

  const uploadVideo = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.upload('/api/services/upload-video', formData);
    const videoUrl = response.data?.videoUrl;
    if (!response.success || !videoUrl) throw new Error('آپلود ویدیو انجام نشد.');
    return resolveServiceMediaUrl(videoUrl);
  };

  const close = async () => {
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    setSwitching(false);
    if (saved) onClose?.();
  };

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [localeResponse, translationResponse] = await Promise.all([
          api.get('/api/locales'),
          api.get(`/api/services/${serviceId}/translations`),
        ]);
        if (!mountedRef.current) return;
        const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
        const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
        const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
        setLocales(localeItems);
        setTranslations(translationItems);
        activeLocaleRef.current = firstLocale;
        setActiveLocale(firstLocale);
        setValue(getWorkDescription(translationItems.find((item) => item.locale === firstLocale), workIndex));
      } catch (loadError) {
        if (mountedRef.current) {
          setError(loadError.data?.message || loadError.message || 'دریافت ترجمه توضیحات نمونه‌کار انجام نشد');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    const timers = saveTimers.current;
    const pending = pendingSaves.current;
    load();
    return () => {
      mountedRef.current = false;
      Object.entries(timers).forEach(([localeCode, timer]) => {
        clearTimeout(timer);
        delete timers[localeCode];
      });
      Object.keys(pending).forEach((localeCode) => {
        persistPending(localeCode);
      });
    };
  }, [persistPending, serviceId, workIndex]);

  return (
    <section className="admin-product-description-translation admin-service-work-description-translation" aria-label="توضیحات چندزبانه نمونه‌کار" dir="rtl">
      <header>
        <span className="admin-product-description-translation__icon"><FiGlobe /><FiImage /></span>
        <div>
          <h4>توضیحات کامل نمونه‌کار در زبان‌های دیگر</h4>
          <p>متن، تصاویر، ویدیوها، لینک‌ها و چینش هر زبان مستقل ذخیره می‌شوند.</p>
        </div>
        <button type="button" className="ui-close-button" onClick={close} disabled={switching} aria-label="بستن ویرایشگر ترجمه">
          {switching ? <FiLoader /> : <FiX />}
        </button>
      </header>

      {loading ? (
        <div className="admin-product-description-translation__loading"><FiLoader /> در حال دریافت زبان‌ها و پیش‌نویس‌ها...</div>
      ) : (
        <>
          <div className="admin-product-description-translation__tabs" role="tablist" aria-label="انتخاب زبان توضیحات نمونه‌کار">
            {enabledLocales.map((locale) => {
              const translation = translations.find((item) => item.locale === locale.code);
              const localizedDescription = getWorkDescription(translation, workIndex);
              const filled = Boolean(
                richTextToPlainText(localizedDescription)
                || /<img\b|data-article-video/i.test(localizedDescription)
              );
              const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeLocale === locale.code}
                  className={`${activeLocale === locale.code ? 'is-active' : ''} ${filled ? 'is-filled' : ''} ${stale ? 'is-stale' : ''}`}
                  key={locale.code}
                  onClick={() => selectLocale(locale.code)}
                  disabled={switching}
                >
                  <b>{locale.code.toUpperCase()}</b>
                  <span>{locale.nativeName}</span>
                  {filled ? <FiCheck /> : null}
                  {stale ? <small>نیاز به بازبینی</small> : null}
                </button>
              );
            })}
          </div>

          {activeLanguage ? (
            <div className="admin-product-description-translation__body">
              <div className="admin-product-description-translation__source">
                <FiAlertCircle />
                <span>
                  <strong>نسخه فارسی به‌صورت خودکار کپی نمی‌شود.</strong>
                  <small>
                    {sourceExcerpt
                      ? `خلاصه «${workTitle || workId || `نمونه‌کار ${workIndex + 1}`}»: ${sourceExcerpt}${sourceText.length > 220 ? '…' : ''}`
                      : 'توضیحات فارسی این نمونه‌کار هنوز خالی است.'}
                  </small>
                </span>
              </div>

              <div className="admin-product-description-translation__editor-head">
                <div>
                  <strong>ویرایشگر {activeLanguage.nativeName}</strong>
                  <small>می‌توانید برای همین زبان متن مستقل بنویسید و تصویر یا ویدیو را دقیقاً در محل دلخواه قرار دهید.</small>
                </div>
                <span className={`is-${saveState}`}>
                  {saveState === 'idle' && (value ? 'آماده ویرایش' : 'پیش‌نویس خالی')}
                  {saveState === 'waiting' && 'در انتظار توقف تایپ...'}
                  {saveState === 'saving' && <><FiLoader /> در حال ذخیره پیش‌نویس...</>}
                  {saveState === 'saved' && <><FiCheck /> پیش‌نویس ذخیره شد</>}
                  {saveState === 'error' && 'ذخیره نشد'}
                </span>
              </div>

              <div dir={activeLanguage.direction || 'rtl'}>
                <ProductRichTextEditor
                  key={`${serviceId}:${workId || workIndex}:${activeLocale}`}
                  editorId={`service-work-description-${serviceId}-${workId || workIndex}-${activeLocale}`}
                  value={value}
                  onChange={(nextValue) => {
                    setValue(nextValue);
                    scheduleSave(nextValue, activeLocale);
                  }}
                  onUploadImage={uploadImage}
                  enableVideo
                  onUploadVideo={uploadVideo}
                  placeholder={`توضیحات کامل نمونه‌کار به زبان ${activeLanguage.nativeName} را اینجا بنویسید...`}
                  ariaLabel={`توضیحات نمونه‌کار به زبان ${activeLanguage.nativeName}`}
                  imageAlt={`${workTitle || 'نمونه‌کار'} - ${activeLanguage.nativeName}`}
                  direction={activeLanguage.direction || 'rtl'}
                  videoContentTitle={workTitle ? `ویدیوی ${workTitle}` : 'ویدیوی نمونه‌کار'}
                  videoDialogTitle="افزودن ویدیو به ترجمه توضیحات نمونه‌کار"
                />
              </div>

              <footer>
                <div>
                  <small>
                    {activeTranslation?.hasUnpublishedChanges
                      ? 'تغییرات در پیش‌نویس ذخیره می‌شوند؛ انتشار نهایی از دکمه ثابت پایین فرم خدمات انجام می‌شود.'
                      : activeTranslation?.status === 'published'
                        ? 'نسخه منتشرشده این زبان به‌روز است.'
                        : 'پس از تکمیل فیلدهای ترجمه، از دکمه ثابت پایین فرم خدمات استفاده کنید.'}
                  </small>
                  {error && <strong role="alert">{error}</strong>}
                </div>
                <div>
                  <button type="button" className={`is-save ${saveState === 'saving' ? 'is-loading' : ''}`} onClick={() => flushLocale(activeLocale)} disabled={saveState === 'saving'}>
                    {saveState === 'saving' ? <FiLoader /> : <FiSave />} ذخیره پیش‌نویس
                  </button>
                </div>
              </footer>
            </div>
          ) : (
            <div className="admin-product-description-translation__empty">
              {error || 'زبان دیگری برای ترجمه فعال نشده است.'}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AdminServiceWorkDescriptionTranslation;
